import { useTheme } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";

import core from "core";
import dates from "core/dates";

import Box from "ui/atom/Box";
import Button from "ui/base/Button";
import Image from "ui/base/Image";

import placeholder from "assets/placeholder.svg";


const ContentSkeleton = ({ style, ...props }) => {
	const theme = useTheme();
	const s = {
		skeleton: {
			backgroundColor: theme.palette.article.skeleton
		}
	};

	return (
		<Box.Column sx={style.root} {...props}>
			<Skeleton variant="rectangular" sx={{ ...style.thumb, ...s.skeleton }} />
			<Box.Column gap={1} sx={style.content}>
				<Box gap={2} sx={style.header}>
					<Skeleton variant="rounded" width={130} sx={{ ...style.logo, ...s.skeleton }} />
					<Typography variant="subtitle1" width={65}>
						<Skeleton sx={s.skeleton} />
					</Typography>
				</Box>
				<Typography variant="caption">
					<Skeleton sx={s.skeleton} width="100%" />
					<Skeleton sx={s.skeleton} width="50%" />
				</Typography>
			</Box.Column>
		</Box.Column>
	);
};

export default function Content({ article, order, toggleBookmark, shadow = false, small = false, navigating, disabled, ...props }) {
	const size = {
		width: {
			small: 195,
			medium: 270
		},
		height: {
			small: 130,
			medium: 180
		}
	};
	const theme = useTheme();

	const style = {
		root: {
			position: "relative",
			overflow: "hidden",
			borderRadius: "6px",
			height: "100%",
			width: small ? size.width.small : size.width.medium,
			boxShadow: shadow ? "0px 3.037px 47.3px 0px rgba(188, 114, 255, 0.39)" : "none",
			cursor: navigating ? "progress" : undefined,
			animation: navigating ? "pulse 1.2s ease-in-out infinite" : undefined,
			"@keyframes pulse": {
				"0%, 100%": { opacity: 1 },
				"50%": { opacity: 0.4 }
			}
		},
		thumbBg: {
			position: "relative",
			width: "100%",
			height: small ? size.height.small : size.height.medium,
			backgroundImage: `url(${article?.thumbUrl})`,
			backgroundPosition: "center",
			backgroundSize: "cover"
		},
		thumb: {
			width: "100%",
			height: small ? size.height.small : size.height.medium,
			objectFit: "contain",
			backdropFilter: "blur(10px)"
		},
		loading: {
			position: "absolute",
			top: "50%", left: "50%",
			translate: "-50% -50%",
			opacity: navigating ? 1 : 0,
			transition: "opacity 200ms ease"
		},
		content: {
			backgroundColor: "background.white",
			p: theme.spacing(1.25),
			flex: "1 1 auto",
			textAlign: "left"
		},
		header: {
			alignItems: "center",
			justifyContent: "space-between"
		},
		title: {
			display: "-webkit-box",
			overflow: "hidden",
			WebkitLineClamp: small ? 2 : 3,
			WebkitBoxOrient: "vertical"
		},
		number: {
			position: "absolute",
			top: 10, left: 10,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			width: "35px",
			height: "35px",
			backgroundColor: "#FC4D0F",
			borderRadius: "6px",
			zIndex: 3
		},
		logo: {
			height: "auto",
			width: {
				xs: "90px",
				sm: "160px"
			}
		},
		bookmark: {
			position: "absolute",
			top: 0,
			right: 0,
			zIndex: 3,
			"svg": {
				filter: "drop-shadow(0 0 3px rgba(0, 0, 0, 0.5))"
			}
		}
	};

	if (!article) return <ContentSkeleton style={style} {...props} />;
	return (
		<>
			<Box.Column sx={style.root} {...props}>
				<Box sx={style.thumbBg}>
					<Image src={article.thumbUrl} fallback={placeholder} sx={style.thumb} />
					{navigating && <CircularProgress size={40} thickness={5} color="tertiary" sx={style.loading} />}
				</Box>
				<Box.Column gap={1} sx={style.content}>
					<Box gap={2} sx={style.header}>
						<Image src={article.partner.logo.dark} sx={style.logo} />
						<Typography variant="subtitle1" color="text.disabled">{article.publishedOn && dates.brief(article.publishedOn)}</Typography>
					</Box>
					<Typography variant="caption" color="text.black" sx={style.title}>{article.title}</Typography>
				</Box.Column>
			</Box.Column>
			{!core.is.undefined(order) && <Typography variant="h4" sx={style.number}>{order + 1}</Typography>}
			{!core.is.undefined(article.bookmarked) &&
				<Button.Icon
					icon="bookmark"
					size={28}
					color={theme.palette.tertiary.main}
					fill={article.bookmarked ? theme.palette.tertiary.main : "none"}
					onClick={() => toggleBookmark(article.id, !article.bookmarked)}
					sx={style.bookmark}
				/>}
		</>
	);
};